import React, { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMutation } from '@apollo/client';
import { Button } from 'react-bootstrap';
import { Dropdown, DropdownToggle, DropdownItem } from '../dropdown';
import { mutations, queriesV2 } from '../../api';
import { getDocumentTemplateById } from '../../api/queries_v2';
import { useNotifications } from '../../notifications';
import { SuccessMessages } from '../../success-messages';
import { TemplateType } from '../../consts/forms-types';
import PropdocsModal from '../modal/PropdocsModal';
import './ActiveInactiveDropdown.scss';

interface ActiveInactiveDropdownProps {
  templateId: string;
  teamId: string;
  isActive: boolean;
  templateType?: TemplateType;
  canManageTemplates: boolean;
  onStatusChange?: () => void;
}

export const ActiveInactiveDropdown: React.FC<ActiveInactiveDropdownProps> = ({
  templateId,
  teamId,
  isActive,
  templateType = TemplateType.DOCUMENT,
  canManageTemplates,
  onStatusChange,
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<boolean | null>(null);
  // Local state to immediately reflect status changes
  const [localIsActive, setLocalIsActive] = useState(isActive);
  const [, { success: notifySuccess, error: notifyError }] = useNotifications();
  const [updateDocumentTemplate] = useMutation(mutations.updateDocumentTemplate as any);

  // Sync local state with prop changes
  useEffect(() => {
    setLocalIsActive(isActive);
  }, [isActive]);

  if (!canManageTemplates) {
    return null;
  }

  const handleStatusChange = (newActiveStatus: boolean) => {
    setPendingStatus(newActiveStatus);
    setShowConfirmModal(true);
  };

  const handleConfirm = async () => {
    if (isUpdating || pendingStatus === null) return;

    const deactivateMessage =
      templateType === TemplateType.CLAUSE ? SuccessMessages.deactivateClause : SuccessMessages.deactivateTemplate;
    const activateMessage =
      templateType === TemplateType.CLAUSE ? SuccessMessages.activateClause : SuccessMessages.activateTemplate;
    const successMessage = pendingStatus ? activateMessage : deactivateMessage;

    setIsUpdating(true);
    setShowConfirmModal(false);

    try {
      await updateDocumentTemplate({
        variables: {
          documentTemplateId: templateId,
          hidden: !pendingStatus, // If making active (pendingStatus=true), set hidden=false
        },
        refetchQueries: [
          {
            query: queriesV2.getDocumentTemplates,
            variables: {
              teamId,
            },
          },
          {
            query: getDocumentTemplateById,
            variables: {
              documentTemplateId: templateId,
            },
          },
        ],
      });

      // Update local state immediately
      setLocalIsActive(pendingStatus);
      notifySuccess({ message: successMessage });
      onStatusChange?.();
    } catch (error) {
      notifyError({ message: error });
    } finally {
      setIsUpdating(false);
      setPendingStatus(null);
    }
  };

  const handleCancel = () => {
    setShowConfirmModal(false);
    setPendingStatus(null);
  };

  const isClause = templateType === TemplateType.CLAUSE;

  const getDropdownTitle = () => {
    return `Change ${isClause ? 'clause' : 'template'} status`;
  };

  const getModalTitle = () => {
    const action = pendingStatus ? 'Active' : 'Inactive';
    const type = isClause ? 'Clause' : 'Template';
    return `Make ${type} ${action}?`;
  };

  const modalMessage = pendingStatus
    ? `This will make the ${isClause ? 'clause' : 'template'} available for creating new documents. Users will see this ${isClause ? 'clause' : 'template'} in their ${isClause ? 'clause' : 'template'} list.`
    : `This will hide the ${isClause ? 'clause' : 'template'} from the ${isClause ? 'clause' : 'template'} list. Existing documents using this ${isClause ? 'clause' : 'template'} will not be affected.`;

  return (
    <>
      <Dropdown className="active-inactive-dropdown" drop="down">
        <Dropdown.Toggle
          id="dropdown-filter-status"
          as={DropdownToggle}
          disabled={isUpdating}
          title={getDropdownTitle()}
        >
          <FormattedMessage
            id="template.filter.status.readout"
            defaultMessage={`Status: {status}`}
            values={{
              status: <span className="status-value">{localIsActive ? 'Active' : 'Inactive'}</span>,
            }}
          />
        </Dropdown.Toggle>

        <Dropdown.Menu>
          <DropdownItem highlightable>
            <div
              className="status-dropdown-item"
              onClick={() => handleStatusChange(!localIsActive)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleStatusChange(!localIsActive);
                }
              }}
            >
              <span className={`status-indicator ${localIsActive ? 'inactive' : 'active'}`}></span>
              <span className="status-action">
                {localIsActive ? (
                  <FormattedMessage id="template.action.deactivate" defaultMessage="Deactivate" />
                ) : (
                  <FormattedMessage id="template.action.activate" defaultMessage="Activate" />
                )}
              </span>
            </div>
          </DropdownItem>
        </Dropdown.Menu>
      </Dropdown>

      <PropdocsModal show={showConfirmModal} setShow={handleCancel} size="sm" title={<span>{getModalTitle()}</span>}>
        <>
          <div style={{ marginBottom: '1.5rem' }}>
            <p>{modalMessage}</p>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', paddingTop: '1.5rem' }}>
            <Button variant="secondary" onClick={handleCancel} disabled={isUpdating}>
              <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
            </Button>
            <Button variant="primary" onClick={handleConfirm} disabled={isUpdating}>
              <FormattedMessage id="common.confirm" defaultMessage="Confirm" />
            </Button>
          </div>
        </>
      </PropdocsModal>
    </>
  );
};
